import { Reveal, Eyebrow } from "./Reveal";
import { Award, Gem, Clock, LifeBuoy, PenTool, ShieldCheck, ReceiptText } from "lucide-react";
import { useLang } from "../i18n";

const REASONS = [
  {
    icon: Award,
    it: ["Oltre 20 anni di esperienza", "Una storia consolidata nel settore edile, cantiere dopo cantiere."],
    en: ["Over 20 years of experience", "A consolidated history in construction, site after site."],
  },
  {
    icon: Gem,
    it: ["Qualità nei materiali", "Materiali di prima scelta e lavorazioni curate in ogni dettaglio."],
    en: ["Quality materials", "First-choice materials and workmanship cared for in every detail."],
  },
  {
    icon: Clock,
    it: ["Rispetto di tempi e costi", "Preventivi chiari e consegne puntuali, senza sorprese."],
    en: ["On time and on budget", "Clear quotes and punctual deliveries, no surprises."],
  },
  {
    icon: LifeBuoy,
    it: ["Assistenza continua", "Un referente dedicato in ogni fase del progetto."],
    en: ["Continuous assistance", "A dedicated contact person at every phase of the project."],
  },
  {
    icon: PenTool,
    it: ["Soluzioni personalizzate", "Ogni intervento è progettato su misura delle tue esigenze."],
    en: ["Tailor-made solutions", "Every project is designed around your needs."],
  },
  {
    icon: ShieldCheck,
    it: ["Assistenza post-intervento", "Restiamo al tuo fianco anche dopo la consegna dei lavori."],
    en: ["After-service support", "We stay by your side even after the works are delivered."],
  },
];

export const Perche = () => {
  const { t, lang } = useLang();

  return (
    <section id="perche-adriatica" data-testid="perche-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 blueprint-dots opacity-[0.04] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("Perché sceglierci", "Why choose us")}</Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            {t("Perché scegliere", "Why choose")} <span className="text-gold-gradient">Adriatica?</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {REASONS.map((r, i) => {
            const [title, text] = lang === "en" ? r.en : r.it;
            return (
              <Reveal key={r.it[0]} delay={0.06 * i}>
                <article
                  data-testid={`reason-card-${i + 1}`}
                  className="trace-card group h-full rounded-2xl bg-[#0E1726] p-7 hover:-translate-y-1.5"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/40 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                    <r.icon size={22} className="text-[#D4AF37]" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl font-medium text-white">{title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="detrazioni-banner"
            className="mt-10 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/5 to-transparent p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 shrink-0">
              <ReceiptText size={22} className="text-[#D4AF37]" />
            </span>
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-medium text-white">
                {t("Detrazioni fiscali sui lavori", "Tax deductions on your works")}
              </h3>
              <p className="mt-2 text-sm sm:text-base text-slate-300 leading-relaxed max-w-3xl">
                {t(
                  "Molti interventi di ristrutturazione e riqualificazione danno diritto alle detrazioni fiscali previste dalla normativa vigente. Con Adriatica hai un interlocutore che ti affianca anche nella parte documentale, dal preventivo alla fine dei lavori.",
                  "Many renovation and upgrade works qualify for the tax deductions provided by current regulations. With Adriatica you have a partner who supports you with the paperwork too, from quote to completion."
                )}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
